import { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { QueryClientContext, useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BRAND, CARE } from './careTheme';
import { useApiActivity } from './useApiActivity';

/**
 * The thin brand-coloured bar along the top of the page while anything is working: a query, a mutation,
 * or a direct API call from a step's handler. It waits a beat before showing so a fast round trip does
 * not flicker, mirroring the static public pages' `.busy-bar`.
 */
export function BusyBar({ delayMs = 200 }: { delayMs?: number }) {
  // The query hooks throw without a QueryClientProvider (unit tests render a page on its own), so
  // TanStack's counts are read by a child that only mounts when a client is present.
  const hasQueryClient = useContext(QueryClientContext) != null;
  const apiCalls = useApiActivity();
  return hasQueryClient ? <BusyBarWithQueries apiCalls={apiCalls} delayMs={delayMs} /> : <BusyBarView active={apiCalls > 0} delayMs={delayMs} />;
}

function BusyBarWithQueries({ apiCalls, delayMs }: { apiCalls: number; delayMs: number }) {
  const active = apiCalls + useIsFetching() + useIsMutating() > 0;
  return <BusyBarView active={active} delayMs={delayMs} />;
}

function BusyBarView({ active, delayMs }: { active: boolean; delayMs: number }) {
  const { t } = useTranslation('portal');
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) { setShown(false); return; }
    const id = window.setTimeout(() => setShown(true), delayMs);
    return () => window.clearTimeout(id);
  }, [active, delayMs]);
  if (!shown) return null;
  return (
    <LinearProgress
      aria-label={t('pbWorking', 'Working…')}
      sx={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: (theme) => theme.zIndex.appBar + 1,
        bgcolor: 'transparent',
        '& .MuiLinearProgress-bar': { backgroundImage: CARE.gradient },
      }}
    />
  );
}

/** What a public care page shows while its code is still downloading: the brand and a ring, never a blank screen. */
export function PublicRouteFallback() {
  const { t } = useTranslation('portal');
  return (
    <Box role="status" aria-live="polite" sx={{ minHeight: '100vh', bgcolor: CARE.ground, display: 'grid', placeItems: 'center', px: 2 }}>
      <Box sx={{ display: 'grid', justifyItems: 'center', gap: 2 }}>
        <Box component="img" src={BRAND.logo} alt={BRAND.name} sx={{ height: 40, width: 'auto' }} />
        <CircularProgress size={28} sx={{ color: CARE.action }} />
        <Typography variant="body2" sx={{ color: CARE.muted }}>{t('pbLoading', 'Loading…')}</Typography>
      </Box>
    </Box>
  );
}
